import React, { useContext, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRange } from '@mui/x-date-pickers-pro/models';

import { translate as t } from 'hooks/useTranslations';
import Modal from 'components/_layouts/Modal';
import { UsersListParams } from '.';
import { FilterAlt } from '@mui/icons-material';
import RolePicker from './RolePicker';
import GroupPicker from './GroupPicker';
import { AppContext } from 'views/App/context';

export type UsersFiltersProps = {
  organizationId: string;
  params: UsersListParams;
  updateParams: (updatedParams: UsersListParams) => void;
};

const UsersFilters: React.FC<UsersFiltersProps> = ({ organizationId, params, updateParams }: UsersFiltersProps) => {
  const { hasPermission } = useContext(AppContext);

  const [showFilters, setShowFilters] = useState(false);
  const [status, setStatus] = useState<string>(params.status ?? '');
  const [roles, setRoles] = useState<string[]>(params.roles ?? []);
  const [groups, setGroups] = useState<string[]>(params.groups ?? []);
  const [createdAtRange, setCreatedAtRange] = useState<DateRange<Dayjs> | null>(
    params.createdAtStart || params.createdAtEnd
      ? () => [dayjs(params.createdAtStart), dayjs(params.createdAtEnd)]
      : null
  );
  const [lastLoginAtRange, setLastLoginAtRange] = useState<DateRange<Dayjs> | null>(
    params.lastLoginAtStart || params.lastLoginAtEnd
      ? () => [dayjs(params.lastLoginAtStart), dayjs(params.lastLoginAtEnd)]
      : null
  );

  const handleApplyFilters = () => {
    const newParams: UsersListParams = {
      groups,
      roles,
      status,
    };
    if (createdAtRange && createdAtRange[0] && createdAtRange[1]) {
      if (createdAtRange[0].isAfter(createdAtRange[1])) {
        return; // Show error message
      }
      newParams.createdAtStart = createdAtRange[0].format('YYYY-MM-DD');
      newParams.createdAtEnd = createdAtRange[1].format('YYYY-MM-DD');
    }
    if (lastLoginAtRange && lastLoginAtRange[0] && lastLoginAtRange[1]) {
      if (lastLoginAtRange[0].isAfter(lastLoginAtRange[1])) {
        return; // Show error message
      }
      newParams.lastLoginAtStart = lastLoginAtRange[0].format('YYYY-MM-DD');
      newParams.lastLoginAtEnd = lastLoginAtRange[1].format('YYYY-MM-DD');
    }
    updateParams(newParams);
    setShowFilters(false);
  };

  return (
    <>
      <Button
        onClick={() => setShowFilters(true)}
        size='small'
        variant='outlined'
        startIcon={<FilterAlt />}
      >
        {t('reporting.users.actions.filter')}
      </Button>
      <Modal
        title={`${t('reporting.users.filters.title')}`}
        size='sm'
        actions={
          <>
            <Button
              color='neutral'
              onClick={() => setShowFilters(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleApplyFilters}
            >
              {t('reporting.users.actions.applyFilters')}
            </Button>
          </>
        }
        open={showFilters}
        onClose={() => setShowFilters(false)}
      >
        <Stack>
          <FormControl sx={{ p: 0 }}>
            <FormLabel>{t('reporting.users.filters.status')}</FormLabel>
            <RadioGroup
              row
              value={status}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setStatus((event.target as HTMLInputElement).value)
              }
              sx={{
                '& .MuiRadio-root': {
                  padding: '2px 8px',
                },
              }}
            >
              <FormControlLabel
                key='status_all'
                value=''
                control={<Radio />}
                label={t('reporting.users.status.ALL')}
              />
              <FormControlLabel
                key='status_active'
                value='ACTIVE'
                control={<Radio />}
                label={t('reporting.users.status.ACTIVE')}
              />
              <FormControlLabel
                key='status_inactive'
                value='INACTIVE'
                control={<Radio />}
                label={t('reporting.users.status.INACTIVE')}
              />
            </RadioGroup>
          </FormControl>
          {hasPermission('listRoles', organizationId) && (
            <FormControl>
              <FormLabel>{t('reporting.users.filters.roles')}</FormLabel>
              <RolePicker
                id='roles_filter'
                organizationId={organizationId}
                value={roles ?? []}
                onChange={(value) => setRoles(value)}
                allowMultiple
                allowBlank
              />
            </FormControl>
          )}
          {hasPermission('listGroups', organizationId) && (
            <FormControl>
              <FormLabel>{t('reporting.users.filters.groups')}</FormLabel>
              <GroupPicker
                id='groups_filter'
                organizationId={organizationId}
                value={groups ?? []}
                onChange={(value) => setGroups(value)}
                allowMultiple
                allowBlank
              />
            </FormControl>
          )}
          <FormControl>
            <FormLabel>{t('reporting.users.filters.createdAt')}</FormLabel>
            <DateRangePicker
              value={createdAtRange ?? undefined}
              onChange={(newValue) => setCreatedAtRange(newValue)}
              sx={{
                '& .MuiFormControl-root': {
                  paddingTop: 0,
                },
                '& .MuiFormLabel-root': {
                  display: 'none',
                },
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t('reporting.users.filters.lastLoginAt')}</FormLabel>
            <DateRangePicker
              value={lastLoginAtRange ?? undefined}
              onChange={(newValue) => setLastLoginAtRange(newValue)}
              sx={{
                '& .MuiFormControl-root': {
                  paddingTop: 0,
                },
                '& .MuiFormLabel-root': {
                  display: 'none',
                },
              }}
            />
          </FormControl>
        </Stack>
      </Modal>
    </>
  );
};

export default UsersFilters;
